import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGuard } from '@ohpen/react-authz';

import { lenderFirmsGetFirmFcaData, FcaResponseResult, FirmFcaData } from '@pmp/api';
import { notification } from '@ohpen/enso-ui';
import { getApiErrorMessage } from '@pmp/utils';
import { TabProps } from '../interfaces';
import { TabContent } from './TabContent';
import { RegistrationDetails } from './RegistrationDetails';
import { ContactDetails } from './ContactDetails';
import { TradingNames } from './TradingNames';
import { Payments } from './Payments';
import { Packaging } from './Packaging';
import { Status } from './Status';
import { showFcaNotification } from '../utils';
import { ProcFeeBoost } from './ProcFeeBoost';

export const FirmDetailsTab: React.FC<TabProps> = ({ form, mode, selectsOptions, defaultComplianceStatus, entityId, errorRows, setDisableSave }) => {
  const { t } = useTranslation();
  const [fcaModifiedFields, setFcaModifiedFields] = useState<FirmFcaData | null | undefined>(null);
  const { allowed: legacyProcFeeAssignments } = useGuard({ flags: ['enableLegacyFirmProcurationFeeAssignments'] });

  const populateData = useCallback(
    (frn: number): Promise<void> => {
      return lenderFirmsGetFirmFcaData({ path: { frn } })
        .then(({ data }) => {
          const { result, registrationDetails, contactDetails, tradingNames } = data;
          if (!result) return;
          const blockedFromDeactivation: string[] = [];

          if (result === FcaResponseResult.OK || result === FcaResponseResult.PARTIAL_DATA) {
            const fcaNames = tradingNames ?? [];
            const fcaNameMap = new Map(fcaNames.map(tn => [tn.name?.trim().toLowerCase() ?? '', tn]));
            const existing: Record<string, unknown>[] = form?.getFieldValue('tradingNames') ?? [];
            const existingNameSet = new Set(existing.map(row => (row.name as string | undefined)?.trim().toLowerCase() ?? ''));

            const updatedExisting = existing.map(row => {
              const fcaEntry = fcaNameMap.get((row.name as string | undefined)?.trim().toLowerCase() ?? '');
              const fcaIsActive = fcaEntry ? (fcaEntry.isActive ?? false) : false;
              // An address linked to intermediaries cannot be deactivated: the change request is
              // rejected and the Status switch is disabled, so applying FCA's inactive status here
              // would leave a form the user can neither save nor repair.
              if (!fcaIsActive && row.isUsed === true && row.isActive !== false) {
                blockedFromDeactivation.push((row.name as string | undefined) ?? '');
                return { ...row };
              }
              return { ...row, isActive: fcaIsActive };
            });
            const newFromFca = fcaNames
              .filter(tn => !existingNameSet.has(tn.name?.trim().toLowerCase() ?? ''))
              .map(tn => ({ ...tn }));

            form?.setFieldsValue({
              registrationDetails: {
                ...registrationDetails,
                type: registrationDetails?.type ?? 'NOT_FCA_REGISTERED',
              },
              contactDetails: { ...contactDetails },
              tradingNames: [...updatedExisting, ...newFromFca],
            });
            // setFieldsValue does not fire the form's onValuesChange, so the Crud footer would
            // otherwise keep Save disabled after a populate.
            setDisableSave?.(false);

            setFcaModifiedFields(data);
          }

          if (result === FcaResponseResult.REFERENCE_NOT_FOUND) {
            form?.setFieldsValue({
              registrationDetails: {
                ...registrationDetails,
                type: registrationDetails?.type ?? 'NOT_FCA_REGISTERED',
              },
            });
            setDisableSave?.(false);
          }

          showFcaNotification(result, t, notification());

          if (blockedFromDeactivation.length) {
            notification().warning({
              title: t('lender.firms.labels.tradingNamesNotDeactivated', { names: blockedFromDeactivation.filter(Boolean).join(', ') }),
            });
          }
        })
        .catch(error => {
          notification().error({ title: getApiErrorMessage(error) });
        });
    },
    [form, t, setDisableSave],
  );

  const removeFcaModifiedField = useCallback((key: keyof FirmFcaData, nestedKey?: string) => {
    setFcaModifiedFields(prev => {
      const next = { ...prev };
      if (nestedKey && next[key]) {
        const nested = { ...(next[key] as Record<string, unknown>) };
        delete nested[nestedKey];
        next[key] = nested as never;
      } else {
        delete next[key];
      }
      return next;
    });
  }, []);

  return (
    <TabContent>
      <RegistrationDetails
        form={form}
        mode={mode}
        selectsOptions={selectsOptions}
        entityId={entityId}
        populateFcaData={populateData}
        fcaModifiedFields={fcaModifiedFields}
        removeFcaModifiedField={removeFcaModifiedField}
      />
      <ContactDetails
        form={form}
        mode={mode}
        selectsOptions={selectsOptions}
        fcaModifiedFields={fcaModifiedFields}
        removeFcaModifiedField={removeFcaModifiedField}
        setDisableSave={setDisableSave}
      />
      <Payments form={form} mode={mode} selectsOptions={selectsOptions} />
      {!legacyProcFeeAssignments && <ProcFeeBoost form={form} mode={mode} selectsOptions={selectsOptions} />}
      <Packaging form={form} mode={mode} selectsOptions={selectsOptions} />
      <Status form={form} mode={mode} selectsOptions={selectsOptions} defaultComplianceStatus={defaultComplianceStatus} />
      <TradingNames
        form={form}
        mode={mode}
        selectsOptions={selectsOptions}
        fcaModifiedFields={fcaModifiedFields}
        removeFcaModifiedField={removeFcaModifiedField}
        errorRows={errorRows}
        setDisableSave={setDisableSave}
      />
    </TabContent>
  );
};
